import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import AmountFilter from '~/components/filters/AmountFilter';
import CollapsibleFilters, { AdditionalFilters, PrimaryFilters } from '~/components/filters/CollapsibleFilters';
import CounterpartFilter from '~/components/filters/CounterpartFilter';
import DocumentPaymentStatusFilter from '~/components/filters/DocumentPaymentStatusFilter';
import DocumentTypeFilter from '~/components/filters/DocumentTypeFilter';
import InlineSearchFilter from '~/components/filters/InlineSearchFilter';
import { DateRange } from '~/components/inputs/DatePicker/CalendarRangePopover';
import DateRangePicker from '~/components/inputs/DatePicker/DateRangePicker';
import Text from '~/components/typographies/Text';
import IconRefresh from '~/icons/icon-refresh.svg?react';
import { useIsDirty, useResetFilters, useUpdateFilter } from '~/state/tableFilters';
import { DocumentsQueryFilters } from '~/types/document';
import styles from './DocumentSearchFilters.module.scss';

interface Props {
  filters: DocumentsQueryFilters;
  onFiltersChange?: (filters: DocumentsQueryFilters) => void;
}

const filterToDateRange = ([start, end]: [string | null, string | null]) => ({
  start: start ? new Date(start) : new Date(),
  end: end ? new Date(end) : new Date(),
});
const dateRangeToFilter = (value: DateRange | null) => {
  if (!value) return undefined;
  return [value.start.toISOString(), value.end.toISOString()] as [string, string];
};

const DocumentSearchFilters = ({ filters }: Props) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.associate_document' });
  const updateFilter = useUpdateFilter<DocumentsQueryFilters>();
  const resetFilters = useResetFilters();
  const hasFiltersApplied = useIsDirty();

  return (
    <CollapsibleFilters className={styles.filters}>
      <PrimaryFilters className={clsx(styles.primaryFiltersRow, styles.filtersRow)} variant="compact">
        <div className={styles.textFilter}>
          <InlineSearchFilter
            autoFocus
            className={styles.inlineSearchFilter}
            name="search"
            value={filters.search || ''}
            label={t('search_documents')}
            onChange={updateFilter}
          />
        </div>
        <CounterpartFilter
          className={styles.counterpartFilter}
          name="counterpart"
          value={filters.counterpart?.length ? (filters.counterpart[0] ?? null) : ''}
          label={t('counterpart')}
          onChange={(val) => updateFilter('counterpart', val ? [val] : undefined)}
        />
        <AmountFilter
          name="totalAmount"
          showFlowDirection={false}
          onChange={updateFilter}
          value={filters.totalAmount ? [filters.totalAmount[0], filters.totalAmount[1]] : [null, null]}
          popoverPlacement="bottom right"
        />
      </PrimaryFilters>

      <AdditionalFilters className={styles.filtersRow}>
        <DocumentTypeFilter
          name="documentTypes"
          label={t('document_type_label')}
          value={filters.documentTypes || []}
          onChange={updateFilter}
          hiddenTypes={[]}
          clearable
        />
        <DateRangePicker
          value={filters.creationDate ? filterToDateRange(filters.creationDate) : null}
          label={t('creation_date_label')}
          onChange={(value) => updateFilter('creationDate', dateRangeToFilter(value))}
          clearable
        />
        <DocumentPaymentStatusFilter
          name="paymentStatus"
          value={filters.paymentStatus || null}
          onChange={updateFilter}
          clearable
        />
      </AdditionalFilters>

      {hasFiltersApplied && (
        <div>
          <Button onClick={resetFilters} Icon={IconRefresh} variant="ghost" size="medium">
            <Text variant="body2" weight="semibold">
              {t('reset_filters')}
            </Text>
          </Button>
        </div>
      )}
    </CollapsibleFilters>
  );
};

export default DocumentSearchFilters;
